import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import SchoolIDSearchPopup from './SchoolIDSearchPopup';
import { ISAFEVENTURES_ENDPOINT } from '../../util/constant';

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const OuterContainer = styled.div`
  background-color: #fff;
  border-radius: 5px;
@media (max-width: ${breakpoints.phone}px) {
    margin: 0 auto;
    width: 100%;
    max-width: 600px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  @media (max-width: ${breakpoints.phone}px) {
    margin: 0 auto;
    width: 100%;
    max-width: 500px;
  }
`;

const CurriculumHeader = styled.div`
  font-size: 16px;
  font-weight: bold;
  padding: 10px;
  background-color: ${(p) => p.colors.buttonBackground};
  color: ${(p) => p.colors.buttonText};
  border-radius: 5px 5px 0 0;
`;

const CurriculumContainer = styled.div`
  background-color: #ffffff;
  border-radius: 0 0 5px 5px;
  overflow: hidden;
`;

const CurriculumTable = styled.table`
  width: 100%;
  margin: 0 auto;
  border-collapse: collapse;

  @media (max-width: ${breakpoints.phone}px) {
    table-layout: fixed;
    word-wrap: break-word;
  }
`;


const TableHeader = styled.th`
  background-color: #ffffff;
  color: ${(p) => p.colors.buttonBackground};
  font-size: 15px;
  padding: 10px;
  text-align: center;
`;

const TableRow = styled.tr`
  background-color: ${(p) => (p.iseven ? '#dcdcdc' : '#ffffff')};
`;

const TableCell = styled.td`
  padding: 10px;
  text-align: center;
  font-size: 13px;
`;

const SubscriptionWrapper = styled.div`
  margin: 1.5em auto;
  width: 95%;
  background-color: #ffffff;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const SubscriptionInner = styled.div`
  padding: 1em;
  background-color: #dcdcdc;
  font-size: 11px;
`;

const SchoolIdLink = styled.a`
  color: #000;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const SchoolIdList = styled.ul`
  list-style: none;
  margin: 10px 0;
  padding: 0;
`;

const SchoolIdItem = styled.li`
  margin-bottom: 5px;
`;

const parseStatus = (status) => {
  if (status === undefined || status === null) return null;
  if (typeof status === 'number' && !Number.isNaN(status)) return status;
  const parsed = Number.parseInt(String(status), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const isCertifiedStatus = (status) => parseStatus(status) === 1;

const hasDownloadFlag = (grade) => {
  if (!grade) return false;
  if (grade.download_date) return true;
  const downloaded = grade.downloaded;
  return (
    downloaded === '1' ||
    downloaded === 1 ||
    downloaded === true
  );
};

const buildCurriculumUrl = (userId, sessionId) => {
  if (!ISAFEVENTURES_ENDPOINT) return null;
  const baseUrl = `${ISAFEVENTURES_ENDPOINT}/direct/curriculum/search`;
  if (!userId || !sessionId) {
    return baseUrl;
  }
  const params = new URLSearchParams({
    var: String(userId),
    log: String(sessionId),
    destination: 'direct/curriculum/search',
  });
  return `${baseUrl}?${params.toString()}`;
};

export default function CurriculumDownload({ colors, implementation, isLoading, curriculumLink }) {
  const [showPopup, setShowPopup] = useState(false);
  // Ensure we always iterate an array to avoid runtime errors when
  // the store provides a non-array (e.g., object/null) value.
  const teacher_subscriptions = useSelector((s) => {
    const ts = s?.auth?.teacher_subscriptions;
    return Array.isArray(ts) ? ts : [];
  });

  const uniqueSubscriptions = useMemo(() => {
    const map = new Map();
    teacher_subscriptions.forEach((sub) => {
      if (!sub) return;
      const keyRaw =
        sub.school_id ??
        sub.customer_id ??
        sub.customerId ??
        sub.customer_number ??
        sub.id;
      const key =
        keyRaw !== undefined && keyRaw !== null
          ? String(keyRaw)
          : null;
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, sub);
      }
    });
    return Array.from(map.values());
  }, [teacher_subscriptions]);

  if (isLoading) {
    return (
      <OuterContainer>
        <CurriculumHeader colors={colors}>
          Curriculum Download
        </CurriculumHeader>
        <CurriculumContainer>
          <CurriculumTable>
            <tbody>
              <tr>
                <td
                  colSpan={2}
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    fontSize: "12px",
                  }}
                >
                  Loading…
                </td>
              </tr>
            </tbody>
          </CurriculumTable>
        </CurriculumContainer>
      </OuterContainer>
    );
  }

  if (!implementation) {
    return (
      <OuterContainer>
        <CurriculumHeader colors={colors}>
          Curriculum Download
        </CurriculumHeader>
        <CurriculumContainer>
          <CurriculumTable>
            <tbody>
              <tr>
                <td
                  colSpan={2}
                  style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontSize: '11px'
                  }}
                >
                  Access to ISAFE curriculum is restricted to schools,
                  districts and organizations that have a current Subscription
                  and License Agreement. If you believe that you should have
                  access to curriculum under your school/district/organization's
                  purchase of an ISAFE subscription, please contact your
                  school/district/organization administrator. Otherwise, please
                  refer your administrator to our Web site (isafe.org) to learn
                  more about purchasing an ISAFE subscription.
                </td>
              </tr>
            </tbody>
          </CurriculumTable>
        </CurriculumContainer>
      </OuterContainer>
    );
  }

  const { user_id, session_id, grade_codes: rawCodes, school_id } = implementation;
  const grade_codes = Array.isArray(rawCodes) ? rawCodes : [];

  return (
    <OuterContainer>
      <CurriculumHeader colors={colors}>
        Curriculum Download
      </CurriculumHeader>

      <CurriculumContainer>
        <TableWrapper>
          <CurriculumTable>
            <thead>
              <tr>
                <TableHeader colors={colors}>Grade</TableHeader>
                <TableHeader colors={colors}>Curriculum Library</TableHeader>
              </tr>
            </thead>
            <tbody>
              {grade_codes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} style={{ textAlign: 'left' }}>
                    No active curriculum downloads are available at this time.
                  </TableCell>
                </TableRow>
              ) : (
                grade_codes.map((gradeObj, idx) => {
                  const grade = String(gradeObj.grade_code);
                  const effectiveUserId = gradeObj.user_id ?? user_id;
                  const effectiveSessionId = gradeObj.session_id ?? session_id;
                  const fallbackUrl = buildCurriculumUrl(effectiveUserId, effectiveSessionId);
                  const url = curriculumLink ?? fallbackUrl;
                  const certified = isCertifiedStatus(gradeObj.status ?? gradeObj.status1);
                  const downloaded = hasDownloadFlag(gradeObj);
                  const textDecoration = !certified && downloaded ? 'underline' : 'none';
                  const fontWeight = certified ? 'bold' : 'normal';

                  return (
                    <TableRow key={`${grade}-${idx}`} iseven={idx % 2 === 0}>
                      <TableCell>
                        <span
                          style={{
                            textDecoration,
                            fontWeight,
                          }}
                        >
                          {grade}
                        </span>
                      </TableCell>
                      <TableCell>
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download
                          </a>
                        ) : (
                          <span style={{ color: '#555' }}>
                            Download unavailable
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </tbody>
          </CurriculumTable>
        </TableWrapper>
      </CurriculumContainer>

      <SubscriptionWrapper>
        <SubscriptionInner>
          <strong>Subscription School ID's:</strong><br/>
          <strong>
            Click&nbsp;
            <SchoolIdLink onClick={() => setShowPopup(true)}>
              here
            </SchoolIdLink>
            &nbsp;to find your school's ID if it's not listed below.
          </strong>

          <SchoolIdList>
            {uniqueSubscriptions.map((sub) => {
              const displayId =
                sub.school_id ??
                sub.customer_id ??
                sub.customerId ??
                school_id ??
                'N/A';
              const key =
                sub.school_id ??
                sub.customer_id ??
                sub.customerId ??
                displayId;
              return (
                <SchoolIdItem key={String(key)}>
                  {sub.org_name},&nbsp;
                  <strong>School ID {displayId}</strong>
                  &nbsp;({sub.org_city}, {sub.org_state})
                </SchoolIdItem>
              );
            })}
          </SchoolIdList>

          If you cannot find the School ID for your school or organization,
          please contact&nbsp;
          <a href="mailto:support@isafeventures.com">
            support@isafeventures.com
          </a>.
        </SubscriptionInner>
      </SubscriptionWrapper>

      {showPopup && (
        <SchoolIDSearchPopup
          colors={colors}
          onClose={() => setShowPopup(false)}
        />
      )}
    </OuterContainer>
  );
}
